// ═══════════════════════════════════════════════════════════════════════════
// CardsLimpack — Sistema visual "cards Limpack" (brand-book) para el renderer IG
// Fase 1 del brief BRIEF-CLAUDE-CODE-MARKETING-2026-07-10.
//
// Cada template es un componente React PURO que dibuja una card completa a
// tamaño de diseño real (1080×1350 feed 4:5 · 1080×1920 story 9:16) y se escala
// al ancho del contenedor con transform: scale(). El mismo markup/CSS se exporta
// a JPG en Fase 5 (Satori/Puppeteer) → lo que ves en el preview es lo que sale.
//
// Anatomía (decisión Nico 2026-07-10, "cargada §1.2"):
//   pill outlined · wordmark Lim*pack* · isotipo · título · chip personas ·
//   [precio + "IVA INCLUIDO"] · divisor · dot + tagline oficial.
//   Premium = acento gold #C5A059 (aprobado) + cajita de regalo.
//
// variant (kits + beneficio):
//   'brand'  (default) → SIN precio, isotipo grande centrado, curiosidad → click.
//   'precio'           → precio hero + "IVA INCLUIDO", isotipo más chico.
//   Regla por canal: ver app_config.disenador_context (feed→brand, story/ads/catálogo→precio).
//
// Isotipo: <Isotipo/> vector inline (geometría oficial, FLAT sin stroke/shadow).
//   SVGs decorativos (cajita, personas, envase, mapa) provisorios → Fase 2 los
//   muda a src/assets/marketing-svgs/.
// ═══════════════════════════════════════════════════════════════════════════
import { useRef, useState, useLayoutEffect } from 'react'
import { Isotipo, PeopleIcon, GiftBox, BottleFlat, MapPin } from '../../../assets/marketing-svgs'

const TAGLINE = 'Limpieza, resuelta cada mes.'

// ── Tokens de marca ────────────────────────────────────────────────────────
export const LPC = {
  navy: '#1A3461', navyDeep: '#0C162C', teal: '#00A8B5', tealBright: '#2BBBC9',
  gold: '#C5A059', goldBright: '#D9BC7E', sky: '#5BBDD8', bone: '#F8F6F1', white: '#FFFFFF',
}
const ACCENT = { essential: LPC.tealBright, premium: LPC.gold }

// Gradientes de fondo (afinados contra las 6 fotos del GBP)
const BG = {
  essential:
    'radial-gradient(120% 78% at 50% 24%, rgba(43,187,201,.30), transparent 60%),' +
    'linear-gradient(180deg,#0a2231 0%,#0e5560 55%,#128499 100%)',
  premium:
    'radial-gradient(120% 78% at 50% 24%, rgba(197,160,89,.32), transparent 60%),' +
    'linear-gradient(180deg,#0c1526 0%,#4a4534 48%,#b6924f 84%,#caa860 100%)',
  navy:
    'radial-gradient(95% 62% at 50% 30%, rgba(0,168,181,.28), transparent 62%),' +
    'linear-gradient(180deg,#16305a 0%,#0C162C 100%)',
}
// Fills del isotipo sobre fondo navy (la flecha navy sería invisible → mono sky)
const ISO_MONO_NAVY = LPC.sky

// ── SVGs provisorios (→ Fase 2 a marketing-svgs/) ──────────────────────────
function Wordmark({ accent = LPC.teal, size = 34, color = LPC.white }) {
  return <div className="lpc-wordmark" style={{ fontSize: size, color }}>Lim<span style={{ color: accent }}>pack</span></div>
}

// ── Piezas compartidas ─────────────────────────────────────────────────────
function Pill({ label, tier, accent }) {
  if (tier === 'premium')
    return <div className="lpc-pill lpc-pill-fill" style={{ background: accent, color: '#2a2113' }}><span className="lpc-star">★</span> {label}</div>
  return <div className="lpc-pill" style={{ borderColor: accent, color: LPC.white }}>{label}</div>
}
function TopBar({ pill, tier, accent }) {
  return <div className="lpc-top"><Pill label={pill} tier={tier} accent={accent} /><Wordmark accent={accent} /></div>
}
function Tagline({ accent }) {
  return <div className="lpc-tagline"><span className="lpc-dot" style={{ background: accent }} />{TAGLINE}</div>
}

// ── CardKit (Essential / Premium · variant brand|precio) ───────────────────
function CardKit({ slide, esStory, tier }) {
  const variant = slide?.variant === 'precio' ? 'precio' : 'brand'
  const accent = tier === 'premium' ? ACCENT.premium : ACCENT.essential
  const bg = tier === 'premium' ? BG.premium : BG.essential
  const nombre = slide?.titulo || (tier === 'premium' ? 'Kit Dúo' : 'Kit Solo')
  const personas = slide?.personas || slide?.dato_sub || '1–2 personas'
  const precio = slide?.precio || (tier === 'premium' ? '$1.090' : '$990')
  const destacado = slide?.destacado
  const isoSize = variant === 'precio' ? 210 : 380

  return (
    <div className={`lpc ${esStory ? 'lpc-story' : 'lpc-feed'}`} style={{ background: bg }}>
      <TopBar pill={tier === 'premium' ? 'PREMIUM' : 'ESSENTIAL'} tier={tier} accent={accent} />

      <div className="lpc-hero">
        <Isotipo size={isoSize} />
        {tier === 'premium' && <div className="lpc-gift"><GiftBox size={variant === 'precio' ? 92 : 104} /></div>}
      </div>

      <div className="lpc-main">
        {destacado && <div className="lpc-badge" style={{ color: accent }}>{destacado}</div>}
        <div className="lpc-titulo">{nombre}</div>
        <div className="lpc-chip"><PeopleIcon /> <span>{personas}</span></div>

        {variant === 'precio' && (
          <div className="lpc-precio-row">
            <div className="lpc-precio">{precio}<span className="lpc-mes">/mes</span></div>
            <div className="lpc-iva">IVA<br />INCLUIDO</div>
          </div>
        )}

        <div className="lpc-divisor" style={{ background: accent }} />
        <Tagline accent={accent} />
      </div>
    </div>
  )
}

// ── Auto-size titulo según cantidad de palabras (13-jul fix: evitar tapar producto) ─
function tituloClaseAuto(texto, override) {
  if (override) return `lpc-titulo lpc-titulo-${override}`  // manual: sm|md|lg|xl
  const n = String(texto || '').trim().split(/\s+/).filter(Boolean).length
  if (n <= 2) return 'lpc-titulo lpc-titulo-xl'   // 150px
  if (n <= 4) return 'lpc-titulo'                  // 118px
  if (n <= 7) return 'lpc-titulo lpc-titulo-md'    // 85px
  return 'lpc-titulo lpc-titulo-sm'                // 60px
}
function bodyClaseAuto(texto, override) {
  if (override) return `lpc-body lpc-body-${override}`
  const chars = String(texto || '').length
  if (chars <= 60) return 'lpc-body'               // 44px
  if (chars <= 150) return 'lpc-body lpc-body-md'  // 36px
  return 'lpc-body lpc-body-sm'                    // 28px
}

// ── CardLanzamiento ────────────────────────────────────────────────────────
function CardLanzamiento({ slide, esStory }) {
  const accent = ACCENT.essential
  const titulo = slide?.titulo || 'Ya estamos'
  const body = slide?.body || ''    // sin fallback "beta" — Limpack no es beta
  const cta = slide?.dato_principal || '→ limpack.uy'
  return (
    <div className={`lpc ${esStory ? 'lpc-story' : 'lpc-feed'}`} style={{ background: BG.navy }}>
      <div className="lpc-top"><Pill label={slide?.caption_top || 'LANZAMIENTO'} tier="essential" accent={accent} /><Wordmark accent={accent} /></div>
      <div className="lpc-hero lpc-hero-lanz"><Isotipo size={esStory ? 240 : 200} mono={ISO_MONO_NAVY} /></div>
      <div className="lpc-main">
        <div className={tituloClaseAuto(titulo, slide?.titulo_size)}>{titulo}</div>
        {body && <div className={bodyClaseAuto(body, slide?.body_size)}>{body}</div>}
        <div className="lpc-cta" style={{ color: accent }}>{cta.startsWith('→') ? cta : '→ ' + cta}</div>
        <div className="lpc-divisor" style={{ background: accent }} />
        <Tagline accent={accent} />
      </div>
    </div>
  )
}

// ── CardBarrio (uso libre, decisión Nico 2026-07-10) ───────────────────────
function CardBarrio({ slide, esStory }) {
  const accent = ACCENT.essential
  const eyebrow = slide?.eyebrow || slide?.caption_top || (slide?.fecha ? slide.fecha + ' pasamos por' : null)
  const titulo = slide?.barrio || slide?.titulo || ''
  const body = slide?.body || ''
  return (
    <div className={`lpc ${esStory ? 'lpc-story' : 'lpc-feed'}`} style={{ background: BG.navy }}>
      <div className="lpc-top"><Pill label="BARRIO" tier="essential" accent={accent} /><Wordmark accent={accent} /></div>
      <div className="lpc-hero"><MapPin size={esStory ? 260 : 180} accent={accent} /></div>
      <div className="lpc-main">
        {eyebrow && <div className="lpc-eyebrow" style={{ color: accent }}>{eyebrow}</div>}
        {titulo && <div className={tituloClaseAuto(titulo, slide?.titulo_size)}>{titulo}</div>}
        {body && <div className={bodyClaseAuto(body, slide?.body_size)}>{body}</div>}
        <div className="lpc-divisor" style={{ background: accent }} />
        <Tagline accent={accent} />
      </div>
    </div>
  )
}

// ── CardSomosGracioso ──────────────────────────────────────────────────────
function CardSomosGracioso({ slide, esStory }) {
  const accent = ACCENT.essential
  const copy = slide?.titulo || slide?.body || ''
  return (
    <div className={`lpc ${esStory ? 'lpc-story' : 'lpc-feed'} lpc-gracioso`} style={{ background: BG.navy }}>
      <div className="lpc-top"><Isotipo size={70} mono={ISO_MONO_NAVY} /><Wordmark accent={accent} size={30} /></div>
      <div className="lpc-gracioso-copy">{copy}</div>
      <div className="lpc-main lpc-main-tight"><div className="lpc-divisor" style={{ background: accent }} /><Tagline accent={accent} /></div>
    </div>
  )
}

// ── CardBeneficio (regla: SIEMPRE precio — el número es el punto) ───────────
function CardBeneficio({ slide, esStory }) {
  const accent = ACCENT.essential
  const eyebrow = slide?.caption_top || ''
  const numero = slide?.dato_principal || ''
  const cap = slide?.dato_cap || slide?.destacado || ''
  return (
    <div className={`lpc ${esStory ? 'lpc-story' : 'lpc-feed'}`} style={{ background: BG.navy }}>
      <div className="lpc-top"><Pill label="BENEFICIO" tier="essential" accent={accent} /><Wordmark accent={accent} /></div>
      <div className="lpc-hero"><BottleFlat size={esStory ? 240 : 180} accent={accent} /></div>
      <div className="lpc-main">
        {eyebrow && <div className="lpc-eyebrow" style={{ color: accent }}>{eyebrow}</div>}
        {numero && <div className="lpc-numero" style={{ color: accent }}>{numero}</div>}
        {cap && <div className={bodyClaseAuto(cap, slide?.body_size)}>{cap}</div>}
        <div className="lpc-divisor" style={{ background: accent }} />
        <Tagline accent={accent} />
      </div>
    </div>
  )
}

// ── Dispatcher + wrapper escalable ─────────────────────────────────────────
export const CARD_TEMPLATES = {
  card_lanzamiento: CardLanzamiento,
  card_kit_essential: (p) => <CardKit {...p} tier="essential" />,
  card_kit_premium: (p) => <CardKit {...p} tier="premium" />,
  card_barrio: CardBarrio,
  card_somos_gracioso: CardSomosGracioso,
  card_beneficio: CardBeneficio,
}
export function isCardLimpack(template) { return !!CARD_TEMPLATES[template] }

/**
 * Renderiza una card Limpack. Por defecto ocupa el 100% del contenedor y escala
 * el lienzo real de 1080 con transform (ResizeObserver) → encaja en el chasis del
 * preview del Hub o en cualquier grilla sin clipping. Pasá `width` (px) para forzar
 * un ancho fijo (export/thumbnail).
 */
export function CardLimpackFrame({ slide, esStory = false, width }) {
  const Comp = CARD_TEMPLATES[slide?.template] || CardKit
  const designH = esStory ? 1920 : 1350
  const ref = useRef(null)
  const [scale, setScale] = useState(width ? width / 1080 : 0)

  useLayoutEffect(() => {
    if (width) return // ancho fijo → scale ya calculado
    const el = ref.current
    if (!el) return
    const update = () => setScale(el.clientWidth / 1080)
    update()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [width])

  const frameStyle = width
    ? { width, height: designH * scale }
    : { width: '100%', aspectRatio: `1080 / ${designH}` }

  return (
    <div ref={ref} className="lpc-frame" style={frameStyle}>
      <div className="lpc-scaler" style={{ transform: `scale(${scale})` }}>
        <Comp slide={slide} esStory={esStory} />
      </div>
    </div>
  )
}

// ── CSS (se monta una vez con <CardsLimpackStyles/>) ───────────────────────
export const LIMPACK_CARDS_CSS = `
.lpc-frame{position:relative;overflow:hidden}
.lpc-scaler{position:absolute;top:0;left:0;transform-origin:top left}
.lpc{position:relative;box-sizing:border-box;font-family:'Inter',system-ui,-apple-system,'Segoe UI',sans-serif;
  color:#fff;overflow:hidden;display:flex;flex-direction:column;padding:74px 78px 70px}
.lpc-feed{width:1080px;height:1350px}
.lpc-story{width:1080px;height:1920px}
.lpc *{box-sizing:border-box}
.lpc-top{display:flex;align-items:center;justify-content:space-between;gap:20px}
.lpc-pill{display:inline-flex;align-items:center;gap:14px;border:4px solid;border-radius:999px;
  padding:16px 40px;font-weight:800;font-size:34px;letter-spacing:.16em;text-transform:uppercase}
.lpc-pill-fill{border:0}
.lpc-star{font-size:32px;line-height:1}
.lpc-wordmark{font-weight:800;letter-spacing:-.02em;line-height:1}
.lpc-hero{flex:1;display:flex;align-items:center;justify-content:center;position:relative;min-height:0}
.lpc-hero-lanz{flex:1.15}
.lpc-gift{position:absolute;right:60px;bottom:36px}
.lpc-main{display:flex;flex-direction:column;gap:22px}
.lpc-main-tight{gap:18px;margin-top:auto}
.lpc-badge{font-weight:800;font-size:34px;letter-spacing:.02em}
.lpc-eyebrow{font-weight:700;font-size:40px;letter-spacing:.01em}
.lpc-titulo{font-weight:800;font-size:118px;line-height:.96;letter-spacing:-.02em;color:#fff}
.lpc-titulo-xl{font-size:150px !important}
.lpc-titulo-md{font-size:85px !important;line-height:1.02 !important}
.lpc-titulo-sm{font-size:60px !important;line-height:1.05 !important}
.lpc-body-md{font-size:36px !important;line-height:1.3 !important}
.lpc-body-sm{font-size:28px !important;line-height:1.35 !important}
.lpc-numero{font-weight:800;font-size:190px;line-height:.9;letter-spacing:-.03em}
.lpc-chip{display:inline-flex;align-items:center;gap:16px;align-self:flex-start;
  background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.22);border-radius:999px;
  padding:14px 30px;font-weight:600;font-size:40px}
.lpc-body{font-weight:500;font-size:44px;line-height:1.25;color:rgba(255,255,255,.9);max-width:900px}
.lpc-precio-row{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin-top:6px}
.lpc-precio{font-weight:800;font-size:132px;line-height:.9;letter-spacing:-.03em;color:#fff}
.lpc-mes{font-weight:500;font-size:52px;letter-spacing:0;margin-left:10px;color:rgba(255,255,255,.82)}
.lpc-iva{font-weight:800;font-size:26px;line-height:1.1;letter-spacing:.12em;text-align:right;
  color:rgba(255,255,255,.72);padding-bottom:14px}
.lpc-cta{font-weight:800;font-size:56px;letter-spacing:-.01em}
.lpc-divisor{height:5px;width:150px;border-radius:3px;opacity:.85}
.lpc-tagline{display:flex;align-items:center;gap:18px;font-weight:600;font-size:36px;color:${LPC.bone}}
.lpc-dot{width:18px;height:18px;border-radius:50%;display:inline-block}
.lpc-gracioso .lpc-gracioso-copy{flex:1;display:flex;align-items:center;font-weight:800;
  font-size:104px;line-height:1.02;letter-spacing:-.02em;white-space:pre-line;color:#fff}
`
export function CardsLimpackStyles() { return <style>{LIMPACK_CARDS_CSS}</style> }
export default CardLimpackFrame
